import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TODAY } from '../utils/dates';
import EmptyState from './primitives/EmptyState';
import {
  ShoppingCart, ShoppingBag, CheckSquare, Package, Pill, Gift,
  BookOpen, Music, Home, Plane, Lightbulb, Utensils,
  PawPrint, Gamepad2, ClipboardList, Zap,
  Dumbbell, Bike, Heart, Shirt, Wrench, Leaf,
  CircleDot,
} from 'lucide-react';

const LIST_ICON_MAP = {
  ShoppingCart, ShoppingBag, CheckSquare, Package, Pill, Gift,
  BookOpen, Music, Home, Plane, Lightbulb, Utensils,
  PawPrint, Gamepad2, ClipboardList, Zap,
  Dumbbell, Bike, Heart, Shirt, Wrench, Leaf,
};

const LIST_ICON_CATEGORIES = [
  { label: 'everyday',  icons: ['ShoppingCart','ShoppingBag','CheckSquare','Package','Pill','Gift'] },
  { label: 'life',      icons: ['BookOpen','Music','Home','Plane','Lightbulb','Utensils'] },
  { label: 'health',    icons: ['Dumbbell','Bike','Heart','Shirt','Wrench','Leaf'] },
  { label: 'other',     icons: ['PawPrint','Gamepad2','ClipboardList','Zap'] },
];

function ListIconPicker({ selected, onSelect }) {
  return (
    <div style={{
      background: 'var(--s1)', border: '1px solid var(--b2)',
      borderRadius: 'var(--r3)', padding: 'var(--sp3) var(--sp4)',
      marginBottom: 'var(--sp3)',
    }}>
      {/* No icon option */}
      <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-xs)', color: 'var(--t3)', textTransform: 'lowercase', letterSpacing: '.04em', marginBottom: 'var(--sp2)' }}>none</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 44px)', gap: 'var(--sp1)', marginBottom: 'var(--sp2)' }}>
        <button type="button"
          onClick={() => onSelect('')}
          style={{
            width: 44, height: 44,
            background: !selected ? 'var(--accent-w)' : 'var(--s2)',
            border: `1px solid ${!selected ? 'var(--gold)' : 'var(--b2)'}`,
            borderRadius: 'var(--r)', display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', color: !selected ? 'var(--accent)' : 'var(--t3)',
            transition: 'background .1s, border-color .1s',
            WebkitTapHighlightColor: 'transparent',
          }}>
          <CircleDot size={18} strokeWidth={1.5} />
        </button>
      </div>
      {LIST_ICON_CATEGORIES.map(cat => (
        <div key={cat.label}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 'var(--fs-xs)', color: 'var(--t3)', textTransform: 'lowercase', letterSpacing: '.04em', margin: 'var(--sp3) 0 var(--sp2)' }}>{cat.label}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 44px)', gap: 'var(--sp1)' }}>
            {cat.icons.map(name => {
              const Icon = LIST_ICON_MAP[name];
              const isSelected = selected === name;
              return (
                <button key={name} type="button"
                  onClick={() => onSelect(name)}
                  style={{
                    width: 44, height: 44,
                    background: isSelected ? 'var(--accent-w)' : 'var(--s2)',
                    border: `1px solid ${isSelected ? 'var(--gold)' : 'var(--b2)'}`,
                    borderRadius: 'var(--r)', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer',
                    color: isSelected ? 'var(--accent)' : 'var(--t3)',
                    transition: 'background .1s, border-color .1s, color .1s',
                    WebkitTapHighlightColor: 'transparent',
                  }}>
                  <Icon size={18} strokeWidth={1.5} />
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function renderListIcon(iconName, size = 22) {
  if (iconName && LIST_ICON_MAP[iconName]) {
    const Icon = LIST_ICON_MAP[iconName];
    return <Icon size={size} strokeWidth={1.5} />;
  }
  return <CircleDot size={size} strokeWidth={1.5} style={{ color: 'var(--t3)' }} />;
}

function ListsSection({ quickLists=[], setSt, notify }) {
  const [selectedList, setSelectedList] = useState(null);
  const [showNewList, setShowNewList]   = useState(false);
  const [newListForm, setNewListForm]   = useState({ title:"", emoji:"" });
  const [newItem, setNewItem]           = useState("");
  const [editingMeta, setEditingMeta]   = useState(false); // list name/icon edit
  const [editMetaForm, setEditMetaForm] = useState({ title:"", emoji:"" });
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const newItemRef = useRef(null);

  const ICON_MAP = { ShoppingCart, ShoppingBag, CheckSquare, Package, Pill, Gift };
  const PRESET_LISTS = [
    { icon:'ShoppingCart', title:'Groceries'  },
    { icon:'ShoppingBag',  title:'Shopping'   },
    { icon:'CheckSquare',  title:'To-Do'      },
    { icon:'Package',      title:'To Buy'     },
    { icon:'Pill',         title:'Pharmacy'   },
    { icon:'Gift',         title:'Gift Ideas' },
  ];

  const createList = (title, emoji) => {
    const id = `ql${Date.now()}`;
    const newL = { id, title, emoji: emoji||"list", items:[], createdAt:TODAY };
    setSt(p => ({ ...p, quickLists:[...(p.quickLists||[]), newL] }));
    setSelectedList(newL);
    setShowNewList(false);
    setNewListForm({ title:"", emoji:"" });
    notify(`"${title}" list created`);
  };

  const updateList = () => {
    if (!editMetaForm.title.trim() || !selectedList) return;
    setSt(p => ({ ...p, quickLists:(p.quickLists||[]).map(l =>
      l.id===selectedList.id ? { ...l, title:editMetaForm.title.trim(), emoji:editMetaForm.emoji } : l
    )}));
    setSelectedList(p => p ? { ...p, title:editMetaForm.title.trim(), emoji:editMetaForm.emoji } : p);
    setEditingMeta(false);
    notify("List updated");
  };

  const deleteList = id => {
    setSt(p => ({ ...p, quickLists:(p.quickLists||[]).filter(l => l.id!==id) }));
    setSelectedList(null);
    setDeleteConfirm(false);
    notify("List deleted");
  };

  const addItem = () => {
    if (!newItem.trim() || !selectedList) return;
    const item = { id:`li${Date.now()}`, text:newItem.trim(), checked:false, addedAt:TODAY };
    setSt(p => ({
      ...p,
      quickLists:(p.quickLists||[]).map(l =>
        l.id===selectedList.id ? { ...l, items:[...l.items, item] } : l
      )
    }));
    setSelectedList(p => ({ ...p, items:[...p.items, item] }));
    setNewItem("");
    newItemRef.current?.focus();
  };

  const toggleItem = (listId, itemId) => {
    setSt(p => ({
      ...p,
      quickLists:(p.quickLists||[]).map(l =>
        l.id!==listId ? l : { ...l, items:l.items.map(i => i.id===itemId ? {...i,checked:!i.checked} : i) }
      )
    }));
    setSelectedList(p => p ? { ...p, items:p.items.map(i => i.id===itemId ? {...i,checked:!i.checked} : i) } : p);
  };

  const deleteItem = (listId, itemId) => {
    setSt(p => ({
      ...p,
      quickLists:(p.quickLists||[]).map(l =>
        l.id!==listId ? l : { ...l, items:l.items.filter(i => i.id!==itemId) }
      )
    }));
    setSelectedList(p => p ? { ...p, items:p.items.filter(i => i.id!==itemId) } : p);
  };

  const moveItem = (listId, itemId, direction) => {
    const reorder = items => {
      const idx = items.findIndex(i => i.id === itemId);
      if (idx < 0) return items;
      const newIdx = direction === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= items.length) return items;
      const arr = [...items];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    };
    setSt(p => ({
      ...p,
      quickLists:(p.quickLists||[]).map(l =>
        l.id!==listId ? l : { ...l, items:reorder(l.items) }
      )
    }));
    setSelectedList(p => p ? { ...p, items:reorder(p.items) } : p);
  };

  const clearChecked = listId => {
    setSt(p => ({
      ...p,
      quickLists:(p.quickLists||[]).map(l =>
        l.id!==listId ? l : { ...l, items:l.items.filter(i => !i.checked) }
      )
    }));
    setSelectedList(p => p ? { ...p, items:p.items.filter(i => !i.checked) } : p);
    notify("Checked items cleared");
  };

  // Sync selectedList with state changes
  const currentList = selectedList ? quickLists.find(l => l.id===selectedList.id) || selectedList : null;

  // ── Detail view (single list open) ──
  if (currentList) {
    const unchecked = currentList.items.filter(i => !i.checked);
    const checked   = currentList.items.filter(i => i.checked);
    return (
      <div>
        {/* List header */}
        {editingMeta ? (
          // Inline edit form for name + icon
          <div style={{marginBottom:"var(--sp4)",background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:"var(--r3)",padding:"var(--sp4)",display:"flex",flexDirection:"column",gap:"var(--sp3)"}}>
            <div style={{display:"flex",alignItems:"center",gap:"var(--sp3)"}}>
              <div style={{flexShrink:0,color:'var(--t2)',display:'flex',alignItems:'center'}}>{renderListIcon(editMetaForm.emoji, 20)}</div>
              <input value={editMetaForm.title}
                onChange={e => setEditMetaForm(p => ({...p,title:e.target.value}))}
                onKeyDown={e => { if(e.key==='Enter') updateList(); if(e.key==='Escape') setEditingMeta(false); }}
                autoFocus autoComplete="off"
                style={{flex:1,height:40,background:"var(--s3)",border:"1px solid var(--gold)",borderRadius:"var(--r2)",padding:"0 var(--sp3)",fontFamily:"var(--sans)",fontSize:"var(--fs-base)",fontWeight:600,color:"var(--ink)",outline:"none",boxSizing:"border-box"}} />
            </div>
            <ListIconPicker selected={editMetaForm.emoji} onSelect={name => setEditMetaForm(p => ({...p,emoji:name}))} />
            <div style={{display:"flex",alignItems:"center",gap:"var(--sp2)"}}>
              <button onClick={updateList} disabled={!editMetaForm.title.trim()}
                style={{height:32,padding:"0 var(--sp3)",background:editMetaForm.title.trim()?"var(--gold)":"var(--s1)",border:"none",borderRadius:"var(--r2)",color:editMetaForm.title.trim()?"var(--on-gold)":"var(--t3)",fontFamily:"var(--sans)",fontSize:"var(--fs-sm)",fontWeight:600,cursor:editMetaForm.title.trim()?"pointer":"not-allowed",transition:"background .1s"}}>save</button>
              <button onClick={() => setEditingMeta(false)}
                style={{background:"transparent",border:"none",fontFamily:"var(--sans)",fontSize:"var(--fs-sm)",color:"var(--t3)",cursor:"pointer",padding:0}}>cancel</button>
              {!deleteConfirm ? (
                <button onClick={() => setDeleteConfirm(true)}
                  style={{marginLeft:"auto",background:"transparent",border:"none",fontFamily:"var(--sans)",fontSize:"var(--fs-xs)",color:"var(--accent-d)",cursor:"pointer",padding:0}}>delete list</button>
              ) : (
                <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:"var(--sp2)"}}>
                  <span style={{fontFamily:"var(--sans)",fontSize:"var(--fs-xs)",color:"var(--t2)"}}>delete all items too?</span>
                  <button onClick={() => deleteList(currentList.id)}
                    style={{height:28,padding:"0 var(--sp2)",background:"var(--accent-d)",border:"none",borderRadius:"var(--r)",color:"white",fontFamily:"var(--sans)",fontSize:"var(--fs-xs)",fontWeight:600,cursor:"pointer"}}>yes, delete</button>
                  <button onClick={() => setDeleteConfirm(false)}
                    style={{background:"transparent",border:"none",fontFamily:"var(--sans)",fontSize:"var(--fs-xs)",color:"var(--t3)",cursor:"pointer",padding:0}}>cancel</button>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{display:"flex",alignItems:"center",gap:"var(--sp2)",marginBottom:"var(--sp4)"}}>
            <button onClick={() => { setSelectedList(null); setEditingMeta(false); setDeleteConfirm(false); }}
              style={{width:44,height:44,background:"var(--s1)",border:"0.5px solid var(--b2)",borderRadius:"var(--r2)",color:"var(--ink)",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",WebkitTapHighlightColor:"transparent",transition:"background .12s"}}
              onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"}
              onMouseLeave={e=>e.currentTarget.style.background="var(--s1)"}>←</button>
            <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",gap:2}}>
              <div style={{fontSize:"var(--fs-base)",fontWeight:500,color:"var(--ink)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{currentList.title}</div>
              <div style={{fontSize:"var(--fs-xs)",color:"var(--t4)",fontFamily:"var(--sans)"}}>{currentList.items.length} item{currentList.items.length!==1?"s":""}{currentList.items.filter(i=>i.checked).length>0?` · ${currentList.items.filter(i=>i.checked).length} done`:""}</div>
            </div>
            <button onClick={() => { setEditMetaForm({title:currentList.title,emoji:currentList.emoji||""}); setEditingMeta(true); setDeleteConfirm(false); }}
              style={{width:44,height:44,background:"transparent",border:"none",color:"var(--t3)",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"var(--r2)",transition:"color .1s, background .1s",WebkitTapHighlightColor:"transparent"}}
              onMouseEnter={e=>{e.currentTarget.style.color="var(--ink)";e.currentTarget.style.background="var(--s1)";}}
              onMouseLeave={e=>{e.currentTarget.style.color="var(--t3)";e.currentTarget.style.background="transparent";}}
              aria-label="Edit list">✎</button>
          </div>
        )}

        {/* Add item */}
        <div style={{display:"flex", gap:"var(--sp2)", marginBottom:"var(--sp4)"}}>
          <input ref={newItemRef} value={newItem} onChange={e=>setNewItem(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addItem()}
            placeholder={`Add to ${currentList.title}…`}
            autoFocus autoComplete="off"
            style={{flex:1,height:"var(--input-h)",background:"var(--s2)",border:"1px solid var(--b2)",borderRadius:"var(--r2)",color:"var(--ink)",fontFamily:"var(--sans)",fontSize:"var(--fs-md)",padding:"0 var(--sp3)",outline:"none",boxSizing:"border-box",transition:"border-color .12s"}}
          />
          <button style={{background:"var(--accent)",color:"var(--on-accent)",border:"none",borderRadius:"var(--r2)",padding:"0 var(--sp3)",fontFamily:"var(--sans)",fontSize:"var(--fs-sm)",fontWeight:500,cursor:"pointer",minHeight:44,flexShrink:0,textTransform:"lowercase",transition:"opacity 150ms ease",WebkitTapHighlightColor:"transparent",opacity:newItem.trim()?1:0.5}} onClick={addItem} disabled={!newItem.trim()}>
            add
          </button>
        </div>

        {/* Items */}
        {currentList.items.length === 0 ? (
          <EmptyState message="nothing here yet" />
        ) : (
          <div className="card-border">
            {unchecked.map((item, i) => (
              <div key={item.id} style={{display:"flex",alignItems:"center",gap:"var(--sp2)",padding:"var(--sp3) var(--card-p)",borderBottom:i<unchecked.length-1||checked.length>0?"1px solid var(--b1)":"none",minHeight:"var(--row-h)",WebkitTapHighlightColor:"transparent"}}>
                <div onClick={()=>toggleItem(currentList.id,item.id)}
                  style={{width:22,height:22,borderRadius:"var(--r)",border:"1.5px solid var(--b3)",background:"transparent",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s"}}>
                </div>
                <span style={{flex:1,fontSize:"var(--fs-md)",color:"var(--ink)",lineHeight:1.5}}>{item.text}</span>
                <div style={{display:"flex",flexDirection:"column",gap:"var(--sp1)",flexShrink:0}}>
                  <button onClick={()=>moveItem(currentList.id,item.id,"up")} disabled={i===0}
                    style={{width:22,height:18,background:"transparent",border:"none",color:i===0?"var(--b2)":"var(--t3)",cursor:i===0?"default":"pointer",fontSize:"var(--fs-xs)",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>▲</button>
                  <button onClick={()=>moveItem(currentList.id,item.id,"down")} disabled={i===unchecked.length-1}
                    style={{width:22,height:18,background:"transparent",border:"none",color:i===unchecked.length-1?"var(--b2)":"var(--t3)",cursor:i===unchecked.length-1?"default":"pointer",fontSize:"var(--fs-xs)",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>▼</button>
                </div>
                <button onClick={()=>deleteItem(currentList.id,item.id)} style={{width:28,height:28,background:"transparent",border:"none",color:"var(--t4)",cursor:"pointer",fontSize:"var(--fs-md)",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",transition:"color .1s",flexShrink:0}}
                  onMouseEnter={e=>e.currentTarget.style.color="var(--red)"}
                  onMouseLeave={e=>e.currentTarget.style.color="var(--t4)"}>×</button>
              </div>
            ))}
            {checked.map((item, i) => (
              <div key={item.id} style={{display:"flex",alignItems:"center",gap:"var(--sp3)",padding:"var(--sp3) var(--card-p)",borderBottom:i<checked.length-1?"1px solid var(--b1)":"none",minHeight:"var(--row-h)",opacity:.5,WebkitTapHighlightColor:"transparent"}}>
                <div onClick={()=>toggleItem(currentList.id,item.id)}
                  style={{width:20,height:20,borderRadius:"var(--r)",border:"1.5px solid var(--grn)",background:"var(--grnbg)",cursor:"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .12s"}}>
                  <span style={{fontSize:"var(--fs-xs)",color:"var(--grn)",fontWeight:700}}>✓</span>
                </div>
                <span style={{flex:1,fontSize:"var(--fs-md)",color:"var(--t3)",textDecoration:"line-through",lineHeight:1.5}}>{item.text}</span>
                <button onClick={()=>deleteItem(currentList.id,item.id)} style={{width:26,height:26,background:"transparent",border:"none",color:"var(--t4)",cursor:"pointer",fontSize:"var(--fs-md)",display:"flex",alignItems:"center",justifyContent:"center",borderRadius:"50%",flexShrink:0}}>×</button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── List grid view ──
  return (
    <div>
      {/* Preset quick-create */}
      {quickLists.length === 0 && (
        <div className="mb4">
          <div style={{fontSize:"var(--fs-sm)",color:"var(--t3)",fontFamily:"var(--mono)",textTransform:"uppercase",letterSpacing:".12em",marginBottom:"var(--sp3)"}}>Quick start</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:"var(--sp2)"}}>
            {PRESET_LISTS.map(p => {
              const Icon = ICON_MAP[p.icon];
              return (
                <button key={p.title} onClick={() => createList(p.title, '')}
                  style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"var(--sp2)",padding:"var(--sp2)",background:"var(--s1)",border:"0.5px solid var(--b2)",borderRadius:"var(--r3)",cursor:"pointer",transition:"border-color .12s, background .12s",WebkitTapHighlightColor:"transparent"}}
                  onMouseEnter={e=>e.currentTarget.style.background="var(--s2)"}
                  onMouseLeave={e=>e.currentTarget.style.background="var(--s1)"}>
                  <Icon size={28} strokeWidth={1.5} color="var(--ink)" />
                  <span style={{fontSize:"var(--fs-sm)",color:"var(--t2)",fontWeight:500,textAlign:"center",lineHeight:1.2}}>{p.title}</span>
                </button>
              );
            })}
            {/* + new list as 7th square cell */}
            <button onClick={() => setShowNewList(true)}
              style={{aspectRatio:"1",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:"var(--sp2)",padding:"var(--sp2)",background:"transparent",border:"0.5px dashed var(--b2)",borderRadius:"var(--r3)",cursor:"pointer",color:"var(--t3)",transition:"border-color .12s, color .12s",WebkitTapHighlightColor:"transparent"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--b3)";e.currentTarget.style.color="var(--t2)";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--b2)";e.currentTarget.style.color="var(--t3)";}}>
              <span style={{fontSize:"var(--fs-xl)",lineHeight:1,height:"var(--fs-xl)",display:"flex",alignItems:"center",justifyContent:"center"}}>+</span>
              <span style={{fontSize:"var(--fs-sm)",fontWeight:500,textAlign:"center",lineHeight:1.2}}>new list</span>
            </button>
          </div>
        </div>
      )}

      {/* Existing lists grid */}
      {quickLists.length > 0 && (
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"var(--sp3)",marginBottom:"var(--sp4)"}}>
          {quickLists.map(list => {
            const unchecked = list.items.filter(i => !i.checked).length;
            const total = list.items.length;
            return (
              <div key={list.id} onClick={() => setSelectedList(list)}
                style={{background:"var(--s1)",border:"1px solid var(--b1)",borderRadius:"var(--r3)",padding:"var(--sp4)",cursor:"pointer",transition:"border-color .15s",WebkitTapHighlightColor:"transparent"}}
                onMouseEnter={e=>e.currentTarget.style.borderColor="var(--b2)"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="var(--b1)"}>
                <div style={{color:"var(--t2)",marginBottom:"var(--sp2)",display:"flex",alignItems:"center"}}>{renderListIcon(list.emoji, 22)}</div>
                <div style={{fontSize:"var(--fs-base)",fontWeight:600,color:"var(--ink)",marginBottom:"var(--sp1)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{list.title}</div>
                <div style={{fontSize:"var(--fs-sm)",color:"var(--t3)",fontFamily:"var(--sans)"}}>{total===0?"empty":`${total} item${total!==1?"s":""}`}</div>
                {total > 0 && (
                  <div style={{marginTop:"var(--sp2)",height:2,background:"var(--b2)",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",background:"var(--grn)",borderRadius:2,width:`${total>0?((total-unchecked)/total)*100:0}%`,transition:"width .3s"}} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New list form — shown when quickLists.length > 0 or triggered from grid */}
      {showNewList ? (
        <div className="card">
          <div style={{fontSize:"var(--fs-sm)",fontWeight:600,color:"var(--ink)",marginBottom:"var(--sp3)"}}>new list</div>
          <ListIconPicker
            selected={newListForm.emoji}
            onSelect={name => setNewListForm(p => ({...p, emoji: name}))}
          />
          <div className="field mb0" style={{marginBottom:"var(--sp3)"}}>
            <label>list name</label>
            <input value={newListForm.title} onChange={e=>setNewListForm(p=>({...p,title:e.target.value}))}
              placeholder="e.g. groceries, weekend errands…"
              onKeyDown={e=>e.key==="Enter"&&newListForm.title.trim()&&createList(newListForm.title,newListForm.emoji)}
              autoFocus autoComplete="off" />
          </div>
          <div className="row-g2">
            <button className="btn btn-gold btn-sm" disabled={!newListForm.title.trim()} onClick={()=>createList(newListForm.title,newListForm.emoji)}>create list</button>
            <button className="btn btn-sm" onClick={()=>{setShowNewList(false);setNewListForm({title:"",emoji:""});}}>cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm" style={{width:"100%",justifyContent:"center",borderStyle:"dashed"}} onClick={()=>setShowNewList(true)}>
          + new list
        </button>
      )}
    </div>
  );
}

// ─── GOALS ────────────────────────────────────────────────────────────────────

export default ListsSection;
